// components/GST.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import Papa from 'papaparse'; // Import papaparse for CSV generation

import Table from '../../shared/components/layout/Table';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import NoDataFound from '../components/layout/NoDataFound';
import { AccountsReportCsvDownloadButton } from '../components/AccountsReportCsvDownloadButton';
import { useAccountsReportToolbar } from '../contexts/AccountsReportToolbarContext';
import PaginationComponent from '../../shared/components/PaginationComponent';
import { transformData } from '../../super-admin/utils/transformData';
import { getAccountGstReports } from '../services/api/implementation/impl';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const GST = () => {
  const userId = Cookies.get('userId');
  const { setTabEndAction } = useAccountsReportToolbar();
  /*
   * -------------------------------------------------
   * Component State
   * -------------------------------------------------
   */
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // Items per page
  const [isDownloading, setIsDownloading] = useState(false); // State for download status
  const [dotCount, setDotCount] = useState(0); // State for dots animation

  /*
   * -------------------------------------------------
   * Fetching GST Reports from the API (Server-Side Pagination)
   * -------------------------------------------------
   */
  const {
    data: gstResponse,
    isFetching: isFetchingGST,
    isError,
    error
  } = useQuery({
    queryKey: ['gstReports', page, limit],
    queryFn: () => getAccountGstReports(page, limit),
    keepPreviousData: true
  });

  // If your API returns { data: [...], meta: {...} }
  const allGSTReports = gstResponse?.data || [];
  const totalItems = gstResponse?.meta?.total || 0;
  const totalPages = Math.ceil(totalItems / limit);

  /*
   * -------------------------------------------------
   * Transform the Data for Table
   * -------------------------------------------------
   */
  const gstDataFormat = [
    'id',
    'UnitName',
    'ProjectName',
    'BlockName',
    'CustomerName',
    'AmountPaid',
    'GST'
  ];

  const processedGSTReports = allGSTReports.map((report) => ({
    id: report.id || 'no-id',
    UnitName: report.unit_name || 'N/A',
    ProjectName: report.project_name || 'N/A',
    BlockName: report.block_name || 'N/A',
    CustomerName: report.customer_name || 'N/A',
    AmountPaid: report.amount_paid !== undefined ? `₹ ${report.amount_paid}` : '₹ 0',
    GST: report.gst_price !== undefined ? `₹ ${report.gst_price}` : '₹ 0'
  }));

  const gstData = processedGSTReports.map((row) => transformData(row, gstDataFormat));

  /*
   * -------------------------------------------------
   * Handlers
   * -------------------------------------------------
   */
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  /**
   * Handle Download CSV using papaparse
   */
  const handleDownload = async () => {
    if (isDownloading) return; // Prevent multiple clicks

    setIsDownloading(true);
    try {
      // Fetch all data with page=1 and limit=1000
      const response = await getAccountGstReports(1, 1000);
      const gstReports = response?.data || [];

      if (gstReports.length === 0) {
        alert('No data available to download.');
        return;
      }

      // Define CSV headers
      const headers = [
        'Unit Name',
        'Project Name',
        'Block Name',
        'Customer Name',
        'Amount Paid',
        'GST'
      ];

      // Map data to CSV rows as objects without the currency symbol
      const rows = gstReports.map((report) => ({
        'Unit Name': report.unit_name || 'N/A',
        'Project Name': report.project_name || 'N/A',
        'Block Name': report.block_name || 'N/A',
        'Customer Name': report.customer_name || 'N/A',
        'Amount Paid': report.amount_paid !== undefined ? report.amount_paid : 0,
        GST: report.gst_price !== undefined ? report.gst_price : 0
      }));

      // Convert to CSV using papaparse
      const csv = Papa.unparse({
        fields: headers,
        data: rows
      });

      // Create a Blob from the CSV content
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

      // Create a temporary link to trigger the download
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', `gst_reports.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Download failed:', downloadError);
      alert('Failed to download the file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  /*
   * -------------------------------------------------
   * Dots Animation Effect
   * -------------------------------------------------
   */

  // Track page view
  useEffect(() => {
    mixpanel.track('G S T Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    let interval;
    if (isDownloading) {
      interval = setInterval(() => {
        setDotCount((prev) => (prev < 3 ? prev + 1 : 0));
      }, 500);
    } else {
      setDotCount(0);
    }

    return () => clearInterval(interval);
  }, [isDownloading]);

  const renderDownloadingText = () => {
    return `Downloading${'.'.repeat(dotCount)}`;
  };

  const handleDownloadRef = useRef(handleDownload);
  handleDownloadRef.current = handleDownload;

  useEffect(() => {
    setTabEndAction(
      <AccountsReportCsvDownloadButton
        onClick={() => handleDownloadRef.current()}
        disabled={isDownloading}
        statusText={isDownloading ? renderDownloadingText() : null}
      />
    );
    return () => setTabEndAction(null);
  }, [setTabEndAction, isDownloading, dotCount]);

  /*
   * -------------------------------------------------
   * Render
   * -------------------------------------------------
   */
  return (
    <div className="w-full flex flex-col z-5 bg-gray-100 overflow-hidden">
      {/* GST Reports Table */}
      <div className="w-full h-full">
        {isFetchingGST ? (
          <div className="pt-12 text-center">
            <p>Loading...</p>
          </div>
        ) : isError ? (
          <div className="pt-12 text-center text-red-500">
            <p>Error: {error.message}</p>
          </div>
        ) : gstData && gstData.length > 0 ? (
          <GridWrapper width={100}>
            <div className="min-h-[26rem] h-[calc(100vh-18rem)]  relative">
              <Table
                headers={[
                  'Unit Name',
                  'Project Name',
                  'Block Name',
                  'Customer Name',
                  'Amount Paid',
                  'GST'
                ]}
                data={gstData}
                identifier="id"
                isViewEnable={false}
                isDeleteEnable={false}
                isEditEnable={false}
                isWrap={true}
              />
            </div>
            <div className="relative pt-3">
              <PaginationComponent
                page={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isFetching={isFetchingGST}
              />
            </div>
          </GridWrapper>
        ) : (
          <NoDataFound disabledBtn screenHeight={false} />
        )}
      </div>
    </div>
  );
};

export default GST;
